/**
 * Time of Use API
 * Endpoint: /systems/{systemID}/timeofuse/settings
 * Source: gridx-api.yaml (TimeOfUseSettings schema)
 */

import { httpClient } from './httpClient'
import type { ApiResponse } from '../types'

export interface TimeOfUseSettings {
  enabled: boolean
  enabledBatteryChargeFromGrid: boolean
  enabledDischargeFlexibilityToGrid: boolean
  enabledPVCurtailment: boolean
  excludeAssetIDs: string[]
  systemKind: 'HEMS' | 'CLOUD_CONNECTOR'
}

/**
 * Get ToU settings for a system.
 * Returns null data if the system has no ToU settings (204).
 * Returns NOT_FOUND error if the system does not exist (404).
 */
export async function getSettings(systemID: string): Promise<ApiResponse<TimeOfUseSettings | null>> {
  try {
    const response = await httpClient.getClient().get<TimeOfUseSettings>(
      `/systems/${systemID}/timeofuse/settings`,
      { headers: { Accept: 'application/vnd.gridx.v2+json' } }
    )
    // 204 No Content — no ToU settings configured
    if (response.status === 204) {
      return { success: true, data: null }
    }
    return { success: true, data: response.data }
  } catch (error: any) {
    if (error.code === 'NOT_FOUND') {
      return { success: false, error: 'System does not exist', errorCode: 'NOT_FOUND' }
    }
    return {
      success: false,
      error: error.message || 'Failed to get ToU settings',
      errorCode: error.code || 'UNKNOWN_ERROR',
    }
  }
}

/**
 * Enable ToU for a system by patching enabled=true.
 */
export async function enable(systemID: string): Promise<ApiResponse<TimeOfUseSettings>> {
  try {
    const response = await httpClient.getClient().patch<TimeOfUseSettings>(
      `/systems/${systemID}/timeofuse/settings`,
      { enabled: true },
      { headers: { Accept: 'application/vnd.gridx.v2+json', 'Content-Type': 'application/vnd.gridx.v2+json' } }
    )
    return { success: true, data: response.data }
  } catch (error: any) {
    return {
      success: false,
      error: error.message || 'Failed to enable ToU',
      errorCode: error.code || 'UNKNOWN_ERROR',
    }
  }
}

/**
 * Disable ToU for a system by patching enabled=false.
 */
export async function disable(systemID: string): Promise<ApiResponse<TimeOfUseSettings>> {
  try {
    const response = await httpClient.getClient().patch<TimeOfUseSettings>(
      `/systems/${systemID}/timeofuse/settings`,
      { enabled: false },
      { headers: { Accept: 'application/vnd.gridx.v2+json', 'Content-Type': 'application/vnd.gridx.v2+json' } }
    )
    return { success: true, data: response.data }
  } catch (error: any) {
    return {
      success: false,
      error: error.message || 'Failed to disable ToU',
      errorCode: error.code || 'UNKNOWN_ERROR',
    }
  }
}
